import React, { useEffect, useRef, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  TableSortLabel,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Stack,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { format } from "date-fns";
import { Timestamp } from "firebase/firestore";
import {
  fetchAllUsersPaginated,
  fetchAllOrganizations,
  crmSliceSelector,
} from "../../../redux/features/crmSlice";

export default function CRMUsers() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const crmState = useSelector((state) => crmSliceSelector(state));
  const {
    allUsers,
    allUsersLoading,
    allUsersLastVisible,
    allUsersAllLoaded,
    organizationsMap,
  } = crmState;

  const observerTarget = useRef(null);
  const fetchAttemptedRef = useRef(false);

  // Parse sort params from URL query string
  const sortParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      sortBy: searchParams.get("sortBy") || "createdAt",
      sortOrder: searchParams.get("sortOrder") || "desc",
    };
  }, [location.search]);

  // Parse filter params from URL query string
  const filterParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const filters = { include: {}, exclude: {} };

    searchParams.forEach((value, key) => {
      if (key.startsWith("filter_include_")) {
        const field = key.replace("filter_include_", "");
        filters.include[field] = value.split(",").filter(Boolean);
      } else if (key.startsWith("filter_exclude_")) {
        const field = key.replace("filter_exclude_", "");
        filters.exclude[field] = value.split(",").filter(Boolean);
      }
    });

    return filters;
  }, [location.search]);

  const [contextMenu, setContextMenu] = useState(null);
  const [contextMenuField, setContextMenuField] = useState(null);
  const [contextMenuValue, setContextMenuValue] = useState(null);
  const [chipContextMenu, setChipContextMenu] = useState(null);
  const [chipContextMenuData, setChipContextMenuData] = useState(null);

  useEffect(() => {
    // Fetch organizations if not already fetched (needed for organization names)
    if (!crmState.organizationsFetched && !crmState.organizationsLoading) {
      dispatch(fetchAllOrganizations());
    }
  }, [dispatch, crmState.organizationsFetched, crmState.organizationsLoading]);

  // Track previous sort params, filter params, and pathname to detect changes
  const prevSortParamsRef = useRef(sortParams);
  const prevFilterParamsRef = useRef(filterParams);
  const prevPathnameRef = useRef(location.pathname);
  const prevSearchRef = useRef(location.search);

  useEffect(() => {
    // Check if pathname changed (user navigated away and back)
    const pathnameChanged = prevPathnameRef.current !== location.pathname;
    // Check if search params changed (filters or sort in URL)
    const searchChanged = prevSearchRef.current !== location.search;

    if (pathnameChanged) {
      prevPathnameRef.current = location.pathname;
      fetchAttemptedRef.current = false;
    }

    if (searchChanged) {
      prevSearchRef.current = location.search;
    }

    // Check if sort params changed
    const sortChanged =
      prevSortParamsRef.current.sortBy !== sortParams.sortBy ||
      prevSortParamsRef.current.sortOrder !== sortParams.sortOrder;

    // Check if filter params changed
    const filterChanged =
      JSON.stringify(prevFilterParamsRef.current) !==
      JSON.stringify(filterParams);

    // Initial fetch when component mounts or pathname changes
    const hasNoUsers = (allUsers || []).length === 0;

    if ((sortChanged || filterChanged || searchChanged) && !allUsersLoading) {
      // Sort, filter, or search params changed - always reset and refetch
      prevSortParamsRef.current = sortParams;
      prevFilterParamsRef.current = filterParams;
      fetchAttemptedRef.current = true;
      dispatch(
        fetchAllUsersPaginated({
          reset: true,
          pageSize: 50,
          sortBy: sortParams.sortBy,
          sortOrder: sortParams.sortOrder,
          filters: filterParams,
        }),
      );
    } else if (
      (hasNoUsers || pathnameChanged) &&
      !allUsersLoading &&
      !fetchAttemptedRef.current
    ) {
      // Initial fetch when component mounts or pathname changes
      fetchAttemptedRef.current = true;
      prevSortParamsRef.current = sortParams;
      prevFilterParamsRef.current = filterParams;
      dispatch(
        fetchAllUsersPaginated({
          reset: true,
          pageSize: 50,
          sortBy: sortParams.sortBy,
          sortOrder: sortParams.sortOrder,
          filters: filterParams,
        }),
      );
    }
  }, [
    location.pathname,
    location.search,
    dispatch,
    allUsers,
    allUsersLoading,
    sortParams,
    filterParams,
    crmState.organizationsFetched,
    crmState.organizationsLoading,
  ]);

  // Continuous loading with Intersection Observer
  useEffect(() => {
    const loadMore = () => {
      if (
        !allUsersLoading &&
        !allUsersAllLoaded &&
        allUsersLastVisible &&
        fetchAttemptedRef.current
      ) {
        dispatch(
          fetchAllUsersPaginated({
            lastVisible: allUsersLastVisible,
            pageSize: 50,
            sortBy: sortParams.sortBy,
            sortOrder: sortParams.sortOrder,
            filters: filterParams,
          }),
        );
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [
    allUsersLoading,
    allUsersAllLoaded,
    allUsersLastVisible,
    sortParams,
    dispatch,
    filterParams,
  ]);

  const handleSort = (field) => {
    const searchParams = new URLSearchParams(location.search);
    const currentSortBy = sortParams.sortBy;
    const currentSortOrder = sortParams.sortOrder;

    if (currentSortBy === field) {
      // Toggle order
      const newOrder = currentSortOrder === "asc" ? "desc" : "asc";
      searchParams.set("sortOrder", newOrder);
    } else {
      // New field, default to desc
      searchParams.set("sortBy", field);
      searchParams.set("sortOrder", "desc");
    }

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const handleCellClick = (event, field, rawValue, displayValue) => {
    event.stopPropagation();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
    setContextMenuField(field);
    setContextMenuValue({ rawValue, displayValue });
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
    setContextMenuField(null);
    setContextMenuValue(null);
  };

  const handleFilterBy = () => {
    if (contextMenuField && contextMenuValue) {
      let filterField;
      if (contextMenuField === "organizations") {
        filterField = "organizationId";
      } else {
        filterField = getFirestoreFieldName(contextMenuField);
      }
      if (filterField) {
        const filterValue =
          contextMenuValue.rawValue || contextMenuValue.displayValue;
        const isRawValue = !!contextMenuValue.rawValue;
        toggleFilter(filterField, filterValue, "include", isRawValue);
      }
    }
    handleContextMenuClose();
  };

  const handleExclude = () => {
    if (contextMenuField && contextMenuValue) {
      let filterField;
      if (contextMenuField === "organizations") {
        filterField = "organizationId";
      } else {
        filterField = getFirestoreFieldName(contextMenuField);
      }
      if (filterField) {
        const filterValue =
          contextMenuValue.rawValue || contextMenuValue.displayValue;
        const isRawValue = !!contextMenuValue.rawValue;
        toggleFilter(filterField, filterValue, "exclude", isRawValue);
      }
    }
    handleContextMenuClose();
  };

  const getFirestoreFieldName = (columnName) => {
    const fieldMap = {
      email: "email",
      displayName: "displayName",
      superAdmin: "superAdmin",
      createdAt: "createdAt",
      lastLogin: "lastLogin",
    };
    return fieldMap[columnName] || null;
  };

  const getFilterValue = (field, displayValue) => {
    // For most fields, display value is the same as filter value
    // For superAdmin, convert "Yes"/"No" to "true"/"false"
    if (field === "superAdmin") {
      return displayValue === "Yes" ? "true" : "false";
    }
    return displayValue;
  };

  const getDisplayValue = (field, rawValue) => {
    // Convert raw Firestore value to display value
    if (field === "superAdmin") {
      return rawValue === "true" || rawValue === true ? "Yes" : "No";
    }
    if (field === "organizationId") {
      // Convert organization ID to organization name
      const org = organizationsMap?.[rawValue];
      return org?.name || rawValue;
    }
    return rawValue;
  };

  const toggleFilter = (field, value, type, isRawValue = false) => {
    const filterValue = isRawValue ? value : getFilterValue(field, value);

    const searchParams = new URLSearchParams(location.search);
    const includeKey = `filter_include_${field}`;
    const excludeKey = `filter_exclude_${field}`;

    if (type === "include") {
      const currentInclude = filterParams.include[field] || [];
      const newInclude = currentInclude.includes(filterValue)
        ? currentInclude.filter((v) => v !== filterValue)
        : [...currentInclude, filterValue];

      // Remove from exclude if it's there
      const newExclude = (filterParams.exclude[field] || []).filter(
        (v) => v !== filterValue,
      );

      if (newInclude.length > 0) {
        searchParams.set(includeKey, newInclude.join(","));
      } else {
        searchParams.delete(includeKey);
      }

      if (newExclude.length > 0) {
        searchParams.set(excludeKey, newExclude.join(","));
      } else {
        searchParams.delete(excludeKey);
      }
    } else {
      const currentExclude = filterParams.exclude[field] || [];
      const newExclude = currentExclude.includes(filterValue)
        ? currentExclude.filter((v) => v !== filterValue)
        : [...currentExclude, filterValue];

      // Remove from include if it's there
      const newInclude = (filterParams.include[field] || []).filter(
        (v) => v !== filterValue,
      );

      if (newExclude.length > 0) {
        searchParams.set(excludeKey, newExclude.join(","));
      } else {
        searchParams.delete(excludeKey);
      }

      if (newInclude.length > 0) {
        searchParams.set(includeKey, newInclude.join(","));
      } else {
        searchParams.delete(includeKey);
      }
    }

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const clearAllFilters = () => {
    const searchParams = new URLSearchParams(location.search);
    const keysToDelete = [];
    searchParams.forEach((value, key) => {
      if (
        key.startsWith("filter_include_") ||
        key.startsWith("filter_exclude_")
      ) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => {
      searchParams.delete(key);
    });
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  // Get active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    Object.values(filterParams.include || {}).forEach((values) => {
      count += values.length;
    });
    Object.values(filterParams.exclude || {}).forEach((values) => {
      count += values.length;
    });
    return count;
  }, [filterParams]);

  const handleChipClick = (event, field, value, currentType) => {
    event.stopPropagation();
    setChipContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
    setChipContextMenuData({ field, value, currentType });
  };

  const handleChipContextMenuClose = () => {
    setChipContextMenu(null);
    setChipContextMenuData(null);
  };

  const handleFlipFilter = () => {
    if (chipContextMenuData) {
      const { field, value, currentType } = chipContextMenuData;
      const newType = currentType === "include" ? "exclude" : "include";
      toggleFilter(field, value, newType, true);
    }
    handleChipContextMenuClose();
  };

  // Helper component for filterable table cell
  const FilterableCell = ({
    field,
    value,
    rawValue,
    children,
    organizations,
    ...props
  }) => {
    const firestoreField = getFirestoreFieldName(field);

    // Don't allow filtering on name or email (unique fields)
    const nonFilterableFields = ["displayName", "email"];
    // Organizations are filterable (client-side)
    const isOrganizationField = field === "organizations";

    if (
      (!firestoreField && !isOrganizationField) ||
      nonFilterableFields.includes(field)
    ) {
      return <TableCell {...props}>{children}</TableCell>;
    }

    const isFilterable = isOrganizationField
      ? organizations && organizations.length > 0
      : value !== "N/A" &&
        rawValue !== null &&
        rawValue !== undefined &&
        rawValue !== "N/A";

    const filterField = isOrganizationField ? "organizationId" : firestoreField;
    const hasFilter =
      filterParams.include[filterField]?.length > 0 ||
      filterParams.exclude[filterField]?.length > 0;

    return (
      <TableCell
        {...props}
        onClick={
          isFilterable && !isOrganizationField
            ? (e) => handleCellClick(e, field, rawValue, value)
            : undefined
        }
        sx={{
          cursor: isFilterable ? "pointer" : "default",
          position: "relative",
          ...(isFilterable && {
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }),
          ...(hasFilter && {
            backgroundColor: "action.selected",
          }),
          ...props.sx,
        }}
      >
        {children}
      </TableCell>
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    let date = timestamp;
    if (date?.toDate) {
      return format(date.toDate(), "MMM d, yyyy h:mm a");
    }
    // Handle serialized timestamp
    if (date?.seconds) {
      date = new Timestamp(date.seconds, date.nanoseconds || 0);
      if (date.toDate) {
        return format(date.toDate(), "MMM d, yyyy h:mm a");
      }
    }
    return "N/A";
  };

  const usersList = allUsers || [];

  return (
    <Container>
      <Typography variant="h2" paddingTop={"20px"}>
        Users
      </Typography>

      {/* Active Filters */}
      {activeFilterCount > 0 && (
        <Box marginTop={2} marginBottom={2}>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            alignItems="center"
          >
            <Chip
              label="Clear all"
              onClick={clearAllFilters}
              size="small"
              variant="outlined"
            />
            {Object.entries(filterParams.include || {}).map(([field, values]) =>
              values.map((value) => {
                const displayValue = getDisplayValue(field, value);
                const fieldLabel =
                  field === "email"
                    ? "email"
                    : field === "displayName"
                    ? "name"
                    : field === "superAdmin"
                    ? "super admin"
                    : field === "organizationId"
                    ? "organization"
                    : field === "createdAt"
                    ? "created"
                    : field === "lastLogin"
                    ? "last login"
                    : field;
                return (
                  <Chip
                    key={`include-${field}-${value}`}
                    label={`${fieldLabel}: ${displayValue}`}
                    onClick={(e) => handleChipClick(e, field, value, "include")}
                    onDelete={(e) => {
                      e.stopPropagation();
                      toggleFilter(field, value, "include", true);
                    }}
                    color="success"
                    size="small"
                    icon={<FilterListIcon />}
                    sx={{ cursor: "pointer" }}
                  />
                );
              }),
            )}
            {Object.entries(filterParams.exclude || {}).map(([field, values]) =>
              values.map((value) => {
                const displayValue = getDisplayValue(field, value);
                const fieldLabel =
                  field === "email"
                    ? "email"
                    : field === "displayName"
                    ? "name"
                    : field === "superAdmin"
                    ? "super admin"
                    : field === "organizationId"
                    ? "organization"
                    : field === "createdAt"
                    ? "created"
                    : field === "lastLogin"
                    ? "last login"
                    : field;
                return (
                  <Chip
                    key={`exclude-${field}-${value}`}
                    label={`${fieldLabel}: ${displayValue} (excluded)`}
                    onClick={(e) => handleChipClick(e, field, value, "exclude")}
                    onDelete={(e) => {
                      e.stopPropagation();
                      toggleFilter(field, value, "exclude", true);
                    }}
                    color="error"
                    size="small"
                    icon={<RemoveCircleIcon />}
                    sx={{ cursor: "pointer" }}
                  />
                );
              }),
            )}
          </Stack>
        </Box>
      )}

      {allUsersLoading && usersList.length === 0 && (
        <LinearProgress sx={{ marginTop: 2, marginBottom: 2 }} />
      )}

      {!allUsersLoading &&
        usersList.length === 0 &&
        fetchAttemptedRef.current && (
          <Typography variant="body2" color="text.secondary" marginTop={2}>
            No users found
          </Typography>
        )}

      {usersList.length > 0 && (
        <Box marginTop={4}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortParams.sortBy === "displayName"}
                      direction={
                        sortParams.sortBy === "displayName"
                          ? sortParams.sortOrder
                          : "asc"
                      }
                      onClick={() => handleSort("displayName")}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortParams.sortBy === "email"}
                      direction={
                        sortParams.sortBy === "email"
                          ? sortParams.sortOrder
                          : "asc"
                      }
                      onClick={() => handleSort("email")}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortParams.sortBy === "superAdmin"}
                      direction={
                        sortParams.sortBy === "superAdmin"
                          ? sortParams.sortOrder
                          : "asc"
                      }
                      onClick={() => handleSort("superAdmin")}
                    >
                      Super Admin
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Organizations</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortParams.sortBy === "lastLogin"}
                      direction={
                        sortParams.sortBy === "lastLogin"
                          ? sortParams.sortOrder
                          : "asc"
                      }
                      onClick={() => handleSort("lastLogin")}
                    >
                      Last Login
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortParams.sortBy === "createdAt"}
                      direction={
                        sortParams.sortBy === "createdAt"
                          ? sortParams.sortOrder
                          : "asc"
                      }
                      onClick={() => handleSort("createdAt")}
                    >
                      Created
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersList.map((user) => {
                  const {
                    id,
                    displayName,
                    email,
                    superAdmin,
                    createdAt,
                    lastLogin,
                    organizations = [],
                  } = user;

                  const superAdminDisplay = superAdmin ? "Yes" : "No";
                  const superAdminRawValue =
                    superAdmin === true ? "true" : "false";

                  // Handle lastLogin
                  let lastLoginDate = "Never";
                  let lastLoginRawValue = null;
                  if (lastLogin) {
                    lastLoginRawValue = lastLogin;
                    lastLoginDate = formatDate(lastLogin);
                  }

                  const createdDate = formatDate(createdAt);

                  return (
                    <TableRow key={id}>
                      <FilterableCell
                        field="displayName"
                        value={displayName || "No name"}
                        rawValue={displayName}
                      >
                        <Typography
                          variant="body1"
                          fontWeight="medium"
                          component={Link}
                          to={`/crm/users/${id}`}
                          sx={{
                            textDecoration: "none",
                            color: "primary.main",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {displayName || "No name"}
                        </Typography>
                      </FilterableCell>
                      <FilterableCell
                        field="email"
                        value={email || "N/A"}
                        rawValue={email}
                      >
                        <Typography variant="body2">
                          {email || "N/A"}
                        </Typography>
                      </FilterableCell>
                      <FilterableCell
                        field="superAdmin"
                        value={superAdminDisplay}
                        rawValue={superAdminRawValue}
                      >
                        <Typography variant="body2">
                          {superAdminDisplay}
                        </Typography>
                      </FilterableCell>
                      <FilterableCell
                        field="organizations"
                        value={
                          organizations.length > 0
                            ? organizations
                                .map((org) => org.organizationName)
                                .join(", ")
                            : "None"
                        }
                        rawValue={
                          organizations.length > 0
                            ? organizations[0].organizationId
                            : null
                        }
                        organizations={organizations}
                      >
                        {organizations.length > 0 ? (
                          <Box>
                            {organizations.map((org, idx) => (
                              <Box
                                key={org.organizationId}
                                marginBottom={
                                  idx < organizations.length - 1 ? 1 : 0
                                }
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                  component={Link}
                                  to={`/crm/organizations/${org.organizationId}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Allow link navigation
                                  }}
                                  onContextMenu={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleCellClick(
                                      e,
                                      "organizations",
                                      org.organizationId,
                                      org.organizationName,
                                    );
                                  }}
                                  sx={{
                                    textDecoration: "none",
                                    color: "primary.main",
                                    "&:hover": {
                                      textDecoration: "underline",
                                    },
                                  }}
                                >
                                  {org.organizationName}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {org.roles.join(", ")}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            None
                          </Typography>
                        )}
                      </FilterableCell>
                      <FilterableCell
                        field="lastLogin"
                        value={lastLoginDate}
                        rawValue={lastLoginRawValue}
                      >
                        <Typography variant="body2">{lastLoginDate}</Typography>
                      </FilterableCell>
                      <FilterableCell
                        field="createdAt"
                        value={createdDate}
                        rawValue={createdAt}
                      >
                        <Typography variant="body2">{createdDate}</Typography>
                      </FilterableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Intersection Observer target for continuous loading */}
      {!allUsersAllLoaded && usersList.length > 0 && (
        <Box ref={observerTarget} sx={{ height: 20, marginTop: 2 }} />
      )}

      {allUsersLoading && usersList.length > 0 && (
        <LinearProgress sx={{ marginTop: 2 }} />
      )}

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleFilterBy}>
          <ListItemIcon>
            <FilterListIcon fontSize="small" sx={{ color: "success.main" }} />
          </ListItemIcon>
          <ListItemText
            primary="Filter by..."
            secondary={
              contextMenuValue
                ? `Show only "${contextMenuValue.displayValue}"`
                : ""
            }
          />
        </MenuItem>
        <MenuItem onClick={handleExclude}>
          <ListItemIcon>
            <RemoveCircleIcon fontSize="small" sx={{ color: "error.main" }} />
          </ListItemIcon>
          <ListItemText
            primary="Exclude..."
            secondary={
              contextMenuValue ? `Hide "${contextMenuValue.displayValue}"` : ""
            }
          />
        </MenuItem>
      </Menu>

      {/* Chip Context Menu */}
      <Menu
        open={chipContextMenu !== null}
        onClose={handleChipContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          chipContextMenu !== null
            ? { top: chipContextMenu.mouseY, left: chipContextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleFlipFilter}>
          <ListItemIcon>
            {chipContextMenuData?.currentType === "include" ? (
              <RemoveCircleIcon fontSize="small" sx={{ color: "error.main" }} />
            ) : (
              <FilterListIcon fontSize="small" sx={{ color: "success.main" }} />
            )}
          </ListItemIcon>
          <ListItemText
            primary={
              chipContextMenuData?.currentType === "include"
                ? "Switch to Exclude"
                : "Switch to Include"
            }
            secondary={
              chipContextMenuData?.currentType === "include"
                ? "Hide this value instead"
                : "Show only this value"
            }
          />
        </MenuItem>
      </Menu>
    </Container>
  );
}
